import { useState } from 'react'
import { FiX, FiUpload, FiPlay, FiImage } from 'react-icons/fi'
import toast from 'react-hot-toast'
import AudioPlayer from '../public/AudioPlayer'

export default function AudioManagement({ formData, setFormData }) {
  const [uploading, setUploading] = useState(false)
  const [uploadingBanner, setUploadingBanner] = useState({})

  const handleAudioUpload = async (file) => {
    if (!file) return

    // Validate file type by MIME type or file extension (more comprehensive)
    const validAudioTypes = [
      'audio/mpeg', 'audio/mp3', 'audio/mpeg3',
      'audio/wav', 'audio/wave', 'audio/x-wav', 'audio/vnd.wav',
      'audio/ogg', 'application/ogg', 'audio/ogg; codecs=opus',
      'audio/aac', 'audio/x-aac', 'audio/aacp',
      'audio/m4a', 'audio/x-m4a', 'audio/mp4', 'audio/m4b', 'audio/m4p',
      'audio/webm', 'audio/flac', 'audio/x-flac'
    ]
    
    const fileExtension = file.name.toLowerCase().split('.').pop()
    const validExtensions = ['mp3', 'wav', 'ogg', 'aac', 'm4a', 'm4b', 'm4p', 'webm', 'flac']
    
    const isValidMimeType = validAudioTypes.some(type => 
      file.type.toLowerCase().includes(type.toLowerCase()) || 
      type.toLowerCase().includes(file.type.toLowerCase())
    ) || file.type.startsWith('audio/')
    const isValidExtension = validExtensions.includes(fileExtension)
    
    if (!isValidMimeType && !isValidExtension) {
      console.error('Invalid audio file:', {
        name: file.name,
        type: file.type,
        extension: fileExtension,
        validTypes: validAudioTypes,
        validExtensions
      })
      toast.error(`Please upload a valid audio file (MP3, WAV, OGG, AAC, M4A, WebM, FLAC). Detected type: ${file.type}, extension: .${fileExtension}`)
      return
    }

    // Validate file size (max 50MB for audio)
    const maxSize = 50 * 1024 * 1024
    if (file.size > maxSize) {
      toast.error('Audio file size must be less than 50MB')
      return
    }

    setUploading(true)

    try {
      console.log('Starting audio upload for file:', file.name, 'size:', file.size, 'type:', file.type)
      // Convert file to base64
      const base64 = await convertFileToBase64(file)
      console.log('Base64 conversion successful, creating audio entry')

      // Add to audio array
      const newAudio = {
        id: Date.now().toString(),
        name: '',
        description: '',
        banner: {
          url: '',
          filename: ''
        },
        audioFile: {
          url: base64,
          publicId: '',
          filename: file.name
        }
      }

      setFormData(prev => ({
        ...prev,
        content: {
          ...prev.content,
          audio: [...(prev.content.audio || []), newAudio]
        }
      }))

      console.log('Audio entry added to form data')
      toast.success('Audio file uploaded successfully')
    } catch (error) {
      console.error('Error uploading audio:', error)
      toast.error('Failed to upload audio file')
    } finally {
      setUploading(false)
    }
  }

  const handleBannerUpload = async (audioId, file) => {
    if (!file) return

    // Validate image file type
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
    
    if (!validImageTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, GIF, WebP)')
      return
    }

    // Validate file size (max 5MB for images)
    const maxSize = 5 * 1024 * 1024
    if (file.size > maxSize) {
      toast.error('Image file size must be less than 5MB')
      return
    }

    setUploadingBanner(prev => ({ ...prev, [audioId]: true }))

    try {
      console.log('Starting banner upload for audio:', audioId)
      // Convert file to base64
      const base64 = await convertFileToBase64(file)
      console.log('Banner converted to base64, length:', base64.length)

      // Update audio with banner
      setFormData(prev => ({
        ...prev,
        content: {
          ...prev.content,
          audio: (prev.content.audio || []).map(audio =>
            audio.id === audioId 
              ? {
                  ...audio,
                  banner: {
                    url: base64,
                    filename: file.name
                  }
                }
              : audio
          )
        }
      }))

      console.log('Banner added to audio entry')
      toast.success('Banner uploaded successfully')
    } catch (error) {
      console.error('Error uploading banner:', error)
      toast.error('Failed to upload banner')
    } finally {
      setUploadingBanner(prev => ({ ...prev, [audioId]: false }))
    }
  }

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        console.log('File converted to base64, length:', reader.result.length)
        resolve(reader.result)
      }
      reader.onerror = (error) => {
        console.error('Error converting file to base64:', error)
        reject(error)
      }
    })
  }

  const handleAudioChange = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        audio: (prev.content.audio || []).map(audio =>
          audio.id === id ? { ...audio, [field]: value } : audio
        )
      }
    }))
  }

  const handleRemoveAudio = (id) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        audio: (prev.content.audio || []).filter(audio => audio.id !== id)
      }
    }))
    toast.success('Audio removed')
  }

  const handleRemoveBanner = (id) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        audio: (prev.content.audio || []).map(audio =>
          audio.id === id
            ? {
                ...audio,
                banner: {
                  url: '',
                  filename: ''
                }
              }
            : audio
        )
      }
    }))
    toast.success('Banner removed')
  }

  return (
    <div className="space-y-6">
      {/* Audio Form Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">🎵 Audio Files</h3>
        
        {/* Upload Section */}
        <div className="mb-6 pb-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-600 hover:bg-purple-100 border border-purple-200 rounded cursor-pointer font-medium text-sm transition-colors">
              <FiUpload className="w-4 h-4" />
              {uploading ? 'Uploading...' : 'Upload Audio'}
              <input
                type="file"
                accept="audio/*"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handleAudioUpload(file)
                }}
                className="hidden"
                disabled={uploading}
              />
            </label>
            <p className="text-sm text-gray-600">
              Upload audio files (MP3, WAV, OGG, AAC, M4A, WebM, FLAC). Max 50MB each.
            </p>
          </div>
        </div>

        {/* Audio List */}
        <div className="space-y-4">
          {(formData.content.audio || []).map((audio, index) => {
            const isComplete = audio.name?.trim() && audio.audioFile?.url
            return (
              <div key={audio.id} className={`border rounded-lg p-4 bg-white ${!isComplete ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}>
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-medium text-gray-900">
                        Audio {index + 1}
                        {!isComplete && <span className="text-red-600 ml-2">(Incomplete)</span>}
                      </h4>
                      {/* Remove Button */}
                      <button
                        type="button"
                        onClick={() => handleRemoveAudio(audio.id)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title="Remove audio"
                      >
                        <FiX className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Audio Banner */}
                    <div className="mb-4">
                      <label className="block text-xs font-medium text-gray-600 mb-2">
                        Audio Banner Image
                      </label>
                      {audio.banner?.url ? (
                        <div className="relative group mb-3">
                          <img
                            src={audio.banner.url}
                            alt="Audio Banner"
                            className="w-full h-32 object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveBanner(audio.id)}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Remove banner"
                          >
                            <FiX className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <label className="flex items-center justify-center w-full h-32 px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-400 transition-colors">
                          <div className="text-center">
                            <FiImage className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-xs text-gray-600">
                              {uploadingBanner[audio.id] ? 'Uploading...' : 'Click to upload banner image'}
                            </p>
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0]
                              if (file) handleBannerUpload(audio.id, file)
                            }}
                            className="hidden"
                            disabled={uploadingBanner[audio.id]}
                          />
                        </label>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        JPEG, PNG, GIF, or WebP. Max 5MB.
                      </p>
                    </div>

                    {/* Audio Name */}
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Audio Name *
                      </label>
                      <input
                        type="text"
                        value={audio.name}
                        onChange={(e) => handleAudioChange(audio.id, 'name', e.target.value)}
                        placeholder="Enter audio name (e.g., Song Title, Podcast Episode)"
                        className="input-field w-full"
                        required
                      />
                    </div>

                    {/* Audio Description */}
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Description
                      </label>
                      <textarea
                        value={audio.description}
                        onChange={(e) => handleAudioChange(audio.id, 'description', e.target.value)}
                        placeholder="Brief description of the audio (artist, duration, etc.)"
                        rows="2"
                        className="input-field w-full resize-none"
                      />
                    </div>

                    {/* Audio File */}
                    {audio.audioFile?.url ? (
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Audio File
                        </label>
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded border">
                          <FiPlay className="w-5 h-5 text-purple-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {audio.audioFile.filename || 'Audio file'}
                            </p>
                            <AudioPlayer
                              src={audio.audioFile.url}
                              filename={audio.audioFile.filename}
                              formStyle={{ backgroundColor: '#F3F4F6' }}
                              textStyle={{ color: '#374151' }}
                              buttonStyle={{ backgroundColor: '#3B82F6', color: '#FFFFFF' }}
                              hoverButtonStyle={{ backgroundColor: '#2563EB', color: '#FFFFFF' }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Audio File *
                        </label>
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded border border-dashed border-gray-300">
                          <FiUpload className="w-5 h-5 text-gray-400" />
                          <p className="text-sm text-gray-500">No audio file uploaded</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Empty State */}
        {(formData.content.audio || []).length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <FiUpload className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-sm">No audio files uploaded yet. Click "Upload Audio" above to add your first audio file.</p>
          </div>
        )}

        {/* Add More Audio Button - Shows when there are already audio files */}
        {(formData.content.audio || []).length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => {
                // Trigger the file input to add more audio
                document.querySelector('input[accept="audio/*"]')?.click()
              }}
              className="btn-secondary w-full"
            >
              <FiUpload className="w-4 h-4 inline mr-2" />
              Add More Audio
            </button>
          </div>
        )}
      </div>
    </div>
  )
}